import type Opportunity from '@/types/serializers/Opportunity.ts'
import MetricCard from '../_components/MetricCard.tsx'
import ContentCard from '../_components/ContentCard.tsx'

interface OverviewTabProps {
    opportunity: Opportunity
}


const formatCurrency = (amount: number | null) => {
    if (!amount) return '$0'
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount)
}

const getProbabilityColor = (probability: number | null) => {
    if (!probability) return 'text-slate-600 dark:text-slate-400'
    if (probability >= 80) return 'text-green-600 dark:text-green-400'
    if (probability >= 60) return 'text-blue-600 dark:text-blue-400'
    if (probability >= 40) return 'text-orange-600 dark:text-orange-400'
    return 'text-red-600 dark:text-red-400'
}

const getPriorityColor = (priority: string | null) => {
    if (priority === 'High') return 'text-orange-600 dark:text-orange-400'
    if (priority === 'Medium') return 'text-yellow-600 dark:text-yellow-400'
    return 'text-slate-600 dark:text-slate-400'
}

const getScoreColor = (score: string | null) => {
    if (score === 'High') return 'text-green-600 dark:text-green-400'
    if (score === 'Medium') return 'text-yellow-600 dark:text-yellow-400'
    if (score === 'Low') return 'text-red-600 dark:text-red-400'
    return 'text-slate-600 dark:text-slate-400'
}

const formatClosingPeriod = (months: number | null) => {
    if (!months) return 'Not specified'
    return months === 1 ? '1 Month' : `${months} Months`
}

const formatArrayField = (array: any[] | null) => {
    if (!array || array.length === 0) return 'None specified'
    return array.join(', ')
}

const getStatus = (opportunity: Opportunity) => {
    if (opportunity.discarded) return 'Discarded'
    if (opportunity.starred) return 'Starred'
    if (opportunity.relevant) return 'Relevant'
    if (opportunity.is_new) return 'New'
    return 'Active'
}

const calculateDealRange = (dealValue: number | null) => {
    if (!dealValue) return {min: 0, max: 0}
    const variance = 0.3
    return {
        min: Math.round(dealValue * (1 - variance)),
        max: Math.round(dealValue * (1 + variance))
    }
}

export default function OverviewTab({opportunity}: OverviewTabProps) {


    const dealRange = calculateDealRange(opportunity.deal_value)

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1 space-y-4">
                <MetricCard
                    label="Status"
                    value={getStatus(opportunity)}
                />
                <MetricCard
                    label="Validation Score"
                    value={(opportunity as any).validation_score || 'Not scored'}
                    color={getScoreColor((opportunity as any).validation_score)}
                />
                <MetricCard
                    label="Impact"
                    value={opportunity.priority || 'Medium'}
                    color={getPriorityColor(opportunity.priority)}
                />
                <MetricCard
                    label="Deal Size Midpoint"
                    value={formatCurrency(opportunity.deal_value)}
                    subtext={dealRange.min > 0 ? `Range ${formatCurrency(dealRange.min)} - ${formatCurrency(dealRange.max)}` : undefined}
                />
                <MetricCard
                    label="Validation Score"
                    value={opportunity.validation_score ? `${opportunity.validation_score}%` : 'N/A'}
                    color={getProbabilityColor(Number(opportunity.validation_score))}
                />
                <MetricCard
                    label="Cost-Benefit Ratio"
                    value={(opportunity as any).cost_benefit_ratio || 'Not assessed'}
                    color={getScoreColor((opportunity as any).cost_benefit_ratio)}
                />
                <MetricCard
                    label="Prospected Closing Period"
                    value={formatClosingPeriod((opportunity as any).closing_period)}
                />
            </div>

            <div className="lg:col-span-2">
                <div className="space-y-8">
                    {/* Pain Point & Solution - Equal Height Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <ContentCard
                            title="Pain Point"
                            headline={opportunity.pain_point.title}
                            content={opportunity.pain_point.description}
                            source={opportunity.account?.website}
                        />
                        <ContentCard
                            title="Solution"
                            headline={opportunity.solution.title}
                            content={opportunity.solution.description}
                            source={opportunity.account?.website}
                        />
                    </div>

                    {/* Bottom Section - Matching Products left, Functions & Use Cases stacked right */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <ContentCard
                            title="Product Components"
                            headline="Matching Products or Sub Products"
                            content={formatArrayField(opportunity.product_components)}
                            source={opportunity.account?.website}
                        />

                        <div className="space-y-6">
                            <ContentCard
                                title="Functions to Engage"
                                headline="Key Functions"
                                content={formatArrayField(opportunity.functions_to_engage)}
                                source={opportunity.account?.website}
                            />
                            <ContentCard
                                title="Products"
                                headline="Related Products"
                                content={formatArrayField(opportunity.products)}
                                source={opportunity.account?.website}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}